import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import {
  Repeat,
  Link2,
  PencilLine,
  ShieldAlert,
  PlugZap,
  PlayCircle,
  CheckCircle2,
  XCircle,
} from "lucide-react";

export const Route = createFileRoute("/admin/social/evergreen-guide")({
  component: EvergreenGuidePage,
  ssr: false,
  head: () => ({
    meta: [
      { title: "Using company content · Social · Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function Step({
  n,
  icon: Icon,
  title,
  children,
}: {
  n: number;
  icon: any;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-4 rounded-lg border border-border bg-card p-4">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
        {n}
      </div>
      <div className="min-w-0">
        <h3 className="flex items-center gap-2 text-base font-semibold">
          <Icon className="h-4 w-4 text-primary" /> {title}
        </h3>
        <div className="mt-1 space-y-2 text-sm text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}

function EvergreenGuidePage() {
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="rounded-lg border border-border bg-gradient-to-br from-primary/10 to-transparent p-5">
        <h2 className="flex items-center gap-2 text-xl font-semibold">
          <Repeat className="h-5 w-5 text-primary" /> How to use company evergreen content
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          We load a library of ready-to-post, compliance-approved content straight into your
          account. Your job is simple: connect your own social accounts, swap in your personal{" "}
          <strong>Apply Now</strong> link, and turn the posts on. The content then re-publishes
          automatically on a set schedule — no writing required.
        </p>
      </div>

      <div className="space-y-3">
        <Step n={1} icon={Repeat} title="What we give you">
          <p>
            Your <Link to="/admin/social/evergreen" className="text-primary underline">Evergreen</Link>{" "}
            tab is pre-filled with company posts. Each one is written, reviewed, and approved by our
            team. They arrive <strong>paused</strong> with <strong>no accounts attached</strong> so
            nothing posts until you&apos;re ready.
          </p>
        </Step>

        <Step n={2} icon={PlugZap} title="Connect your social accounts">
          <p>
            Go to <Link to="/admin/social/connect" className="text-primary underline">Connect GHL</Link>{" "}
            and link your GoHighLevel account, then confirm your pages appear under{" "}
            <Link to="/admin/social/accounts" className="text-primary underline">Accounts</Link>.
            Posts can only go out once your own accounts are connected.
          </p>
        </Step>

        <Step n={3} icon={Link2} title="Swap in YOUR Apply Now link">
          <p>
            The only thing you should change in a post is the application link — replace the company
            link with your personal <strong>Apply Now</strong> / referral link so leads are tracked
            to you.
          </p>
          <p>
            In the Evergreen tab, find the link in the post text (it looks like{" "}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">.../apply</code>) and replace it
            with your own. Save, and you&apos;re done.
          </p>
        </Step>

        <Step n={4} icon={PencilLine} title="What you can and cannot edit">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-green-500/30 bg-green-500/5 p-3">
              <p className="flex items-center gap-1.5 font-semibold text-green-700">
                <CheckCircle2 className="h-4 w-4" /> You CAN
              </p>
              <ul className="mt-1 list-disc space-y-1 pl-4 text-xs">
                <li>Replace the apply link with your own.</li>
                <li>Pick which of your accounts each post goes to.</li>
                <li>Adjust how often it repeats (the interval).</li>
                <li>Pause or activate a post.</li>
              </ul>
            </div>
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
              <p className="flex items-center gap-1.5 font-semibold text-destructive">
                <XCircle className="h-4 w-4" /> You must NOT
              </p>
              <ul className="mt-1 list-disc space-y-1 pl-4 text-xs">
                <li>Rewrite or change the wording of the post.</li>
                <li>Remove or alter any disclaimers or compliance language.</li>
                <li>Add claims about guaranteed approval, rates, or funding.</li>
              </ul>
            </div>
          </div>
        </Step>

        <Step n={5} icon={ShieldAlert} title="Why the wording is locked">
          <p>
            Every post and disclaimer is approved for compliance. Changing the text — even slightly —
            can create regulatory risk for you and the company. Keep the copy and disclaimers exactly
            as provided; only the apply link is yours to personalize.
          </p>
        </Step>

        <Step n={6} icon={PlayCircle} title="Attach accounts & turn it on">
          <p>
            On each evergreen post, use <strong>Post to accounts</strong> to select your connected
            pages, then flip the switch to <strong>Active</strong>. The post will publish on its
            schedule. Use <strong>Post now</strong> (the play button) to test one immediately.
          </p>
        </Step>
      </div>

      <div className="rounded-lg border border-border bg-card p-4 text-sm">
        <p className="font-semibold">Quick start</p>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-muted-foreground">
          <li>Connect GHL → confirm your accounts load.</li>
          <li>Open Evergreen → swap the apply link for yours (text stays the same).</li>
          <li>Pick your accounts on each post.</li>
          <li>Switch the post to Active.</li>
        </ol>
        <div className="mt-3">
          <Link
            to="/admin/social/evergreen"
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            <Repeat className="h-4 w-4" /> Go to Evergreen
          </Link>
        </div>
      </div>
    </div>
  );
}
